<template>
  <transition name="job-drawer">
    <div v-if="show && isJobDetails" class="job-drawer-wrapper">
      <div class="job-drawer-backdrop" @click="$emit('close')"></div>
      <aside class="job-drawer" role="dialog" aria-label="Job details">
        <!-- Header -->
        <div class="job-drawer-header bg-primary text-white">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center" v-if="index_selectedJob > -1">
              <button
                class="icon-btn"
                :disabled="!previousJob"
                @click="getJobDetails(previousJob)"
                title="Previous job"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button
                class="icon-btn ml-1"
                :disabled="!nextJob"
                @click="getJobDetails(nextJob)"
                title="Next job"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
              <small class="ml-2 text-white-75">
                {{ index_selectedJob + 1 }} of {{ getJobListForCandidateID.length }}
              </small>
            </div>
            <button class="icon-btn ml-auto" @click="$emit('close')" title="Close">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="d-flex align-items-center">
            <div class="logo-ring" :class="{ 'logo-ring-gradient': isSuperCustomer }">
              <img
                class="job-logo"
                :src="logo"
                @error="$event.target.src = '/img/hospital.png'"
                alt="Organisation"
              />
            </div>
            <div class="ml-3 min-w-0">
              <h5 class="mb-1 fw-700 text-white text-break">
                {{ jobTitle }}
                <i
                  v-if="isFeatured"
                  v-c-tooltip="'Featured Job'"
                  class="fas fa-star featured-star ml-1"
                ></i>
              </h5>
              <div class="small">Job ID: {{ displayReferenceId }}</div>
              <div class="small text-break" v-if="organisation">
                <i class="fa-solid fa-hospital mr-1"></i>{{ organisation }}
              </div>
              <div class="small">
                <i class="fa-solid fa-location-dot mr-1"></i>{{ hospitalLocation }}
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap align-items-center mt-3">
            <span v-if="jobStatus" class="status-chip mr-2 mb-2" :class="{ 'bg-indigo-light': isSuperCustomer }">
              {{ jobStatus }}<template v-if="jobSubStatus"> &middot; {{ jobSubStatus }}</template>
            </span>
            <span
              v-if="jobDetails.reference_type_id && [4, 3].includes(jobDetails.reference_type_id)"
              class="status-chip mr-2 mb-2"
              :class="{ 'bg-indigo-light': isSuperCustomer }"
              v-c-tooltip="{ content: `${jobDetails.matched_comments}`, appendToBody: true }"
            >
              <i class="fa-solid fa-circle-info mr-1"></i>Match info
            </span>
          </div>

          <div class="d-flex flex-wrap align-items-center">
            <!-- Same button styles as the candidate card (CandidateInfo) -->
            <CButton
              class="drawer-btn mr-2"
              :class="isSuperCustomer ? 'bg-indigo-light' : 'bg-white text-primary'"
              @click="navigateToJobDetail"
            >
              <i class="fa-solid fa-arrow-up-right-from-square mr-2"></i>View Job
            </CButton>
            <CDropdown
              v-if="canAct && jobActions.length"
              placement="bottom-end"
              class="drawer-actions"
            >
              <template #toggler>
                <CButton class="drawer-btn" :class="isSuperCustomer ? 'bg-gradient text-white' : 'drawer-btn-light'">
                  Actions<i class="fa-solid fa-chevron-down ml-2"></i>
                </CButton>
              </template>
              <a
                class="dropdown-item cursor-action"
                v-for="data in jobActions"
                :key="data.action_id"
                @click="jobActionEvent(data)"
                :class="{ 'positive-action': checkPositiveAction(data) }"
              >
                {{ data.label }}
              </a>
            </CDropdown>
          </div>
        </div>

        <!-- Body -->
        <div class="job-drawer-body">
          <div class="fact-grid">
            <div class="fact bg-blue-light-2">
              <div class="fact-label">Level</div>
              <div class="fact-value">{{ level }}</div>
            </div>
            <div class="fact bg-blue-light-2">
              <div class="fact-label">Job Status</div>
              <div class="fact-value">{{ publishStatus }}</div>
            </div>
            <div class="fact fact-wide bg-blue-light-2">
              <div class="fact-label">Speciality</div>
              <div class="fact-value">{{ speciality }}</div>
            </div>
            <div class="fact bg-blue-light-2" v-if="!isNurse">
              <div class="fact-label">Sub Speciality</div>
              <div class="fact-value">{{ sub_speciality || "--" }}</div>
            </div>
            <div class="fact bg-blue-light-2" v-else>
              <div class="fact-label">Other Areas of Expertise</div>
              <div class="fact-value">{{ other_areas_of_expertise || "--" }}</div>
            </div>
            <div class="fact bg-blue-light-2">
              <div class="fact-label">Days to Expire</div>
              <div
                class="fact-value"
                :class="{ 'text-danger': daysToExpire === 'Expired' }"
              >
                {{ daysToExpire }}
              </div>
            </div>
          </div>

          <ul class="drawer-tabs" v-if="tabsToShow.length > 1">
            <li
              v-for="(tab, index) in tabsToShow"
              :key="tab"
              :class="{ 'active text-primary': job_step === index }"
              @click="setJobStep(index)"
            >
              {{ tab }}
            </li>
          </ul>

          <div class="f-12">
            <details-section v-if="job_step === 0" :jobDetails="jobDetails" />
            <in-process-section
              v-if="job_step === 1"
              :jobDetails="jobDetails"
              :candidateJobPageInProcessList="getCandidateJobPageInProcessList"
            />
          </div>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import JobDetails from "./JobDetails";

// Slide-over version of JobDetails: same data and actions, drawer layout
export default {
  name: "JobDetailsDrawer",
  extends: JobDetails,
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(["isSuperCustomer"]),
    canAct() {
      return [this.Role.customerAdmin, this.Role.customerRecruiter].includes(
        this.currentUserRole
      );
    },
    // JobDetails' "Alerts" tab has no content, so only show tabs that do
    tabsToShow() {
      return this.isCandidateSupplierFromAccessToken
        ? this.agencyJobTabs
        : ["Details", "In Process"];
    },
    daysToExpire() {
      return this.$options.filters.expiryDate(
        this.jobDetails?.expiry_review_date
      );
    },
  },
  watch: {
    "jobDetails.job.job_id"() {
      this.job_step = 0;
    },
    show(val) {
      if (val) document.addEventListener("keydown", this.onKeydown);
      else document.removeEventListener("keydown", this.onKeydown);
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.job-drawer-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  // Above the fixed app header (1030), below modals (1040+) opened from Actions
  z-index: 1035;
}

.job-drawer-backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(15, 10, 40, 0.35);
}

.job-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.15);
}

.job-drawer-header {
  padding: 16px 20px;
  flex-shrink: 0;
}

.job-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75);
}

.min-w-0 {
  min-width: 0;
}

.job-logo {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: contain;
  background: #fff;
  padding: 4px;
}

.logo-ring {
  flex-shrink: 0;
  padding: 3px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
}

// Same ring as the candidate avatar in CandidateInfo
.logo-ring-gradient {
  background: linear-gradient(135deg, #4c34e9, #ff00ff, #00dfff);
}

.featured-star {
  color: #ffb547;
  font-size: 13px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}

.drawer-btn {
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 14px;
}

.drawer-btn-light {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    color: #fff;
  }
}

.fact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.fact {
  background: #f6f5fc;
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 0;
}

.fact-wide {
  grid-column: 1 / -1;
}

.fact-label {
  font-size: 11px;
  color: #6f6f6f;
  margin-bottom: 2px;
}

.fact-value {
  font-size: 13px;
  font-weight: 500;
  color: #1e1e1e;
  word-break: break-word;
}

.drawer-tabs {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
  border-bottom: 1px solid #e6e3f5;

  li {
    padding: 8px 14px;
    font-size: 13px;
    cursor: pointer;
    color: #6f6f6f;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;

    &.active {
      border-bottom-color: currentColor;
      font-weight: 600;
    }
  }
}

.positive-action {
  background-color: lightgreen;
}

.job-drawer-enter-active,
.job-drawer-leave-active {
  transition: opacity 0.25s ease;

  .job-drawer {
    transition: transform 0.25s ease;
  }
}

.job-drawer-enter,
.job-drawer-leave-to {
  opacity: 0;

  .job-drawer {
    transform: translateX(100%);
  }
}
</style>
